import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';

const SettingsForm = ({ show, onHide, setting, onSubmit, loading = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    value: '',
    type: 'text',
    options: []
  });

  // State for managing clones
  const [clones, setClones] = useState([]);
  const [newClone, setNewClone] = useState({
    bookie: '',
    runner: false,
    restrictProxy: false,
    isSimple: true
  });

  const isBookie = setting && setting.bookie;

  useEffect(() => {
    if (setting) {
      if (isBookie) {
        // Handle bookie data
        setFormData({
          ...setting,
          id: setting.bookie,
          title: setting.bookie,
          description: `Configuration for ${setting.bookie}`,
          type: 'bookie'
        });
        
        // Initialize clones state
        setClones(setting.clones || []);
      } else {
        // Handle regular setting data
        setFormData({
          id: setting.id || '',
          title: setting.title || '',
          description: setting.description || '',
          value: setting.value || '',
          type: setting.type || 'text',
          options: setting.options || []
        });
      }
    } else {
      setFormData({
        id: '',
        title: '',
        description: '',
        value: '',
        type: 'text',
        options: []
      });
      setClones([]);
    }
  }, [setting, isBookie]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
    }));
  };

  const handleCloneChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewClone(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addClone = () => {
    if (!newClone.bookie.trim()) return;

    const cloneToAdd = newClone.isSimple 
      ? newClone.bookie.trim()
      : {
          bookie: newClone.bookie.trim(),
          runner: newClone.runner,
          restrictProxy: newClone.restrictProxy
        };

    setClones(prev => [...prev, cloneToAdd]);
    setNewClone({
      bookie: '',
      runner: false,
      restrictProxy: false,
      isSimple: true
    });
  };

  const removeClone = (index) => {
    setClones(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSubmit = isBookie 
      ? { ...formData, clones }
      : formData;
    onSubmit(dataToSubmit);
  };

  const renderRegularSettingForm = () => {
    const renderValueInput = () => {
      switch (formData.type) {
        case 'toggle':
          return (
            <Form.Check
              type="switch"
              id="value-switch"
              name="value"
              checked={formData.value}
              onChange={handleChange}
              label={formData.value ? 'Enabled' : 'Disabled'}
            />
          );
        case 'select':
          return (
            <Form.Select
              name="value"
              value={formData.value}
              onChange={handleChange}
              required
              className="bg-[#1B2D1A] border-[#3A4C39] text-white"
              style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            >
              {formData.options.map((option) => (
                <option key={option} value={option} className="bg-[#1B2D1A] text-white">
                  {option}
                </option>
              ))}
            </Form.Select>
          );
        case 'number':
          return (
            <Form.Control
              type="number"
              name="value"
              value={formData.value}
              onChange={handleChange}
              required
              className="bg-[#1B2D1A] border-[#3A4C39] text-white"
              style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            />
          );
        default:
          return (
            <Form.Control
              type="text"
              name="value"
              value={formData.value}
              onChange={handleChange}
              required
              className="bg-[#1B2D1A] border-[#3A4C39] text-white"
              style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            />
          );
      }
    };

    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label className="text-[#ABCDDF]">{t('title')}</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="bg-[#1B2D1A] border-[#3A4C39] text-white"
            style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-[#ABCDDF]">{t('description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="bg-[#1B2D1A] border-[#3A4C39] text-white"
            style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-[#ABCDDF]">Value</Form.Label>
          {renderValueInput()}
        </Form.Group>
      </>
    );
  };

  const renderBookieForm = () => {
    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label className="text-[#ABCDDF]">Bookie Name</Form.Label>
          <Form.Control
            type="text"
            name="bookie"
            value={formData.bookie || ''}
            onChange={handleChange}
            required
            className="bg-[#1B2D1A] border-[#3A4C39] text-white"
            style={{ backgroundColor: '#1B2D1A', color: 'white' }}
            readOnly
          />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Max Logins</Form.Label>
              <Form.Control
                type="number"
                name="maxLogins"
                value={formData.maxLogins || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Login Interval</Form.Label>
              <Form.Control
                type="number"
                name="logInInterval"
                value={formData.logInInterval || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Minimum Profit</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="minimumProfit"
                value={formData.minimumProfit || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Maximum Profit</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="maximumProfit"
                value={formData.maximumProfit || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Priority</Form.Label>
              <Form.Control
                type="number"
                name="priority"
                value={formData.priority || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Hard Limit</Form.Label>
              <Form.Control
                type="number"
                name="hardLimit"
                value={formData.hardLimit || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Probability Threshold</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="probabilityThreshold"
                value={formData.probabilityThreshold || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-[#ABCDDF]">Country</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={formData.country || ''}
                onChange={handleChange}
                className="bg-[#1B2D1A] border-[#3A4C39] text-white"
                style={{ backgroundColor: '#1B2D1A', color: 'white' }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Boolean Flags */}
        <div className="mb-3">
          <Form.Label className="text-[#ABCDDF] d-block mb-2">Settings</Form.Label>
          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="shouldRound"
                name="shouldRound"
                checked={formData.shouldRound || false}
                onChange={handleChange}
                label="Should Round"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="needsProtection"
                name="needsProtection"
                checked={formData.needsProtection || false}
                onChange={handleChange}
                label="Needs Protection"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="tracing"
                name="tracing"
                checked={formData.tracing || false}
                onChange={handleChange}
                label="Tracing"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="solo"
                name="solo"
                checked={formData.solo || false}
                onChange={handleChange}
                label="Solo"
                className="mb-2"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="needsExtendedBalanceCheck"
                name="needsExtendedBalanceCheck"
                checked={formData.needsExtendedBalanceCheck || false}
                onChange={handleChange}
                label="Needs Extended Balance Check"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="noBalanceRestriction"
                name="noBalanceRestriction"
                checked={formData.noBalanceRestriction || false}
                onChange={handleChange}
                label="No Balance Restriction"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="needsProtectionForSecondBet"
                name="needsProtectionForSecondBet"
                checked={formData.needsProtectionForSecondBet || false}
                onChange={handleChange}
                label="Needs Protection for Second Bet"
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="blacklisted"
                name="blacklisted"
                checked={formData.blacklisted || false}
                onChange={handleChange}
                label="Blacklisted"
                className="mb-2"
              />
            </Col>
          </Row>
        </div>

        {/* Clones Management Section */}
        <div className="mb-3">
          <Form.Label className="text-[#ABCDDF] d-block mb-2">Clones</Form.Label>
          
          {/* Existing Clones */}
          {clones.length > 0 && (
            <div className="mb-3 p-3 bg-[#1B2D1A] rounded">
              <h6 className="text-[#E8FC8B] text-sm font-semibold mb-2">Current Clones:</h6>
              <div className="space-y-2">
                {clones.map((clone, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-[#2A3C29] rounded">
                    <div className="flex items-center space-x-2">
                      <span className="text-[#E8FC8B] font-medium">
                        {typeof clone === 'string' ? clone : clone.bookie}
                      </span>
                      {typeof clone === 'object' && (
                        <div className="flex space-x-1">
                          {clone.runner !== undefined && (
                            <span className={`px-1 py-0.5 rounded text-xs ${
                              clone.runner ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'
                            }`}>
                              {clone.runner ? 'Runner' : 'No Runner'}
                            </span>
                          )}
                          {clone.restrictProxy && (
                            <span className="px-1 py-0.5 rounded text-xs bg-orange-600 text-white">
                              Restrict Proxy
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeClone(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Clone */}
          <div className="p-3 bg-[#1B2D1A] rounded">
            <h6 className="text-[#E8FC8B] text-sm font-semibold mb-2">Add New Clone:</h6>
            
            <Row className="mb-2">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="text-[#ABCDDF]">Clone Bookie Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="bookie"
                    value={newClone.bookie}
                    onChange={handleCloneChange}
                    placeholder="Enter bookie name"
                    className="bg-[#2A3C29] border-[#3A4C39] text-white"
                    style={{ backgroundColor: '#2A3C29', color: 'white' }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-[#ABCDDF]">Clone Type</Form.Label>
                  <Form.Check
                    type="switch"
                    id="isSimple"
                    name="isSimple"
                    checked={newClone.isSimple}
                    onChange={handleCloneChange}
                    label={newClone.isSimple ? "Simple" : "Advanced"}
                  />
                </Form.Group>
              </Col>
            </Row>

            {!newClone.isSimple && (
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="runner"
                    name="runner"
                    checked={newClone.runner}
                    onChange={handleCloneChange}
                    label="Runner"
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="restrictProxy"
                    name="restrictProxy"
                    checked={newClone.restrictProxy}
                    onChange={handleCloneChange}
                    label="Restrict Proxy"
                  />
                </Col>
              </Row>
            )}

            <Button
              variant="success"
              size="sm"
              onClick={addClone}
              disabled={!newClone.bookie.trim()}
            >
              Add Clone
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg"
      className="text-white"
    >
      <Modal.Header 
        closeButton 
        className="bg-[#2A3C29] border-[#3A4C39]"
      >
        <Modal.Title className="text-[#E8FC8B]">
          {isBookie ? `Edit ${formData.bookie} Configuration` : `${setting ? t('editUpdate') : t('createUpdate')} Setting`}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="bg-[#2A3C29]">
        <Form onSubmit={handleSubmit}>
          {isBookie ? renderBookieForm() : renderRegularSettingForm()}

          <div className="d-flex justify-content-end gap-2">
            <Button 
              variant="secondary" 
              onClick={onHide}
              className="bg-gray-600 border-gray-600"
              disabled={loading}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit"
              className="bg-[#E8FC8B] text-[#1B2D1A] border-[#E8FC8B] hover:bg-[#E8FC8B]/90"
              disabled={loading}
            >
              {loading ? 'Saving...' : t('saveChanges')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default SettingsForm; 